import {
  GiftCouponRedeemStatus,
  GiftCouponSummary,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { GiftOrderForAdmin } from '@shared/api/giftOrder/giftOrder.interface';

import {
  buildGiftItemEmbedMap,
  sortOrdersByPurchasedAt,
} from './deliveredCouponPhoneExcel';

/**
 * 주문검색 결과(여러 주문)에 걸쳐 쿠폰 한 건을 찾아내기 위한 유틸.
 *
 * 응대 상황: "이 번호로 주문한 분의 쿠폰 중 김OO에게 갈 건이 뭐냐"처럼
 * 주문을 특정한 뒤 그 안에서 쿠폰을 골라야 하는 일이 많은데,
 * 쿠폰은 주문별 상세 모달에만 있어 주문 수만큼 모달을 열어봐야 했다.
 * 여기서는 검색된 주문들의 쿠폰을 한 줄로 펴서(flatten) 메모·수령인·번호·코드로
 * 한 번에 검색하고, 번호가 비슷한 수령인 묶음까지 뽑아준다.
 */

/** 쿠폰에 붙어 있는 번호의 출처 — 화면 배지에 그대로 쓴다 */
export type CouponPhoneKind = 'recipient' | 'additional' | 'renewal' | 'refund';

export const couponPhoneKindLabel: Record<CouponPhoneKind, string> = {
  recipient: '신청자',
  additional: '수신번호',
  renewal: '연장수신',
  refund: '환불연락처',
};

export interface CouponPhoneEntry {
  kind: CouponPhoneKind;
  /** 숫자만 남긴 값 (비교·검색용) */
  digits: string;
}

/** 검색된 주문들의 쿠폰을 한 줄로 편 항목 */
export interface FlatCouponEntry {
  coupon: GiftCouponSummary;
  order: GiftOrderForAdmin;
  /** 주문 구분 라벨 (구매일 기준 정렬 순번) — "주문 1" 처럼 표기 */
  orderLabel: string;
  /** 쿠폰 표시용 코드 (code-seq) */
  couponLabel: string;
  itemName: string;
  /** 이 쿠폰에 달린 번호들 (중복 제거) */
  phones: CouponPhoneEntry[];
  /** 검색 대상 텍스트 (소문자·공백 제거 전 원문 결합) */
  searchText: string;
}

const digitsOf = (v: string | undefined | null): string =>
  (v ?? '').replace(/[^0-9]/g, '');

/** 취소·폐기된 쿠폰 (기본적으로 결과에서 흐리게 처리) */
export const isInactiveCoupon = (c: GiftCouponSummary): boolean =>
  c.redeemStatus === GiftCouponRedeemStatus.Canceled ||
  c.redeemStatus === GiftCouponRedeemStatus.Disposed;

const collectPhones = (c: GiftCouponSummary): CouponPhoneEntry[] => {
  const seen = new Set<string>();
  const list: CouponPhoneEntry[] = [];
  const push = (kind: CouponPhoneKind, raw: string | undefined) => {
    const digits = digitsOf(raw);
    if (digits.length < 8 || seen.has(digits)) return;
    seen.add(digits);
    list.push({ kind, digits });
  };
  push('recipient', c.recipient?.phoneNumber);
  push('additional', c.additionalRecipientPhone);
  push('renewal', c.renewalRecipientPhone);
  push('refund', c.refund?.phoneNumber);
  return list;
};

/**
 * 검색 결과 주문들의 쿠폰을 한 줄로 편다.
 * 행 순서는 호출부가 넘긴 주문 순서(보통 구매일 최신순)를 그대로 따르되,
 * 주문 라벨 번호는 **구매일 오래된 순**으로 매긴다 — 번호 기입폼 통합 엑셀의
 * "주문1, 주문2..."와 같은 번호여야 회신받은 엑셀과 대조가 된다.
 */
export const flattenOrderCoupons = (orders: GiftOrderForAdmin[]): FlatCouponEntry[] => {
  const entries: FlatCouponEntry[] = [];
  const labelIndexByOrderId = new Map<string, number>();
  sortOrdersByPurchasedAt(orders).forEach((order, i) =>
    labelIndexByOrderId.set(order._id, i + 1),
  );
  orders.forEach((order) => {
    const embedMap = buildGiftItemEmbedMap([order]);
    for (const coupon of order.coupons ?? []) {
      const itemName = embedMap.get(coupon.giftItem)?.name ?? '';
      const choiceUnitName =
        coupon.selectedChoiceUnit && typeof coupon.selectedChoiceUnit === 'object'
          ? coupon.selectedChoiceUnit.name ?? ''
          : '';
      const phones = collectPhones(coupon);
      entries.push({
        coupon,
        order,
        orderLabel: `주문 ${labelIndexByOrderId.get(order._id) ?? 0}`,
        couponLabel: `${coupon.code}-${coupon.seq}`,
        itemName,
        phones,
        searchText: [
          coupon.memo ?? '',
          coupon.recipient?.name ?? '',
          coupon.code,
          `${coupon.code}-${coupon.seq}`,
          itemName,
          choiceUnitName,
          order.receiverName ?? '',
          order.bugo?.fevent?.deceasedInfo?.name ?? '',
          ...phones.map((p) => p.digits),
        ]
          .join(' ')
          .toLowerCase(),
      });
    }
  });
  return entries;
};

/**
 * 검색어 매칭 — 공백으로 나눈 토큰이 **모두** 걸려야 통과(AND).
 * 숫자 토큰은 번호 뒷자리 검색으로 자주 쓰이므로 번호 숫자열 부분일치까지 본다.
 */
export const matchCouponEntry = (entry: FlatCouponEntry, query: string): boolean => {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  return tokens.every((token) => {
    if (entry.searchText.includes(token)) return true;
    const digits = token.replace(/[^0-9]/g, '');
    if (digits.length >= 2 && token.replace(/[^0-9-]/g, '') === token) {
      return entry.phones.some((p) => p.digits.includes(digits));
    }
    return false;
  });
};

/**
 * 두 번호가 "비슷한가" — 오기입 의심 판정.
 * 편집거리 1 이내(치환/삽입/삭제) 또는 인접 자리 뒤바뀜(전치)을 비슷한 것으로 본다.
 * 완전히 같은 번호는 여기서 false (중복은 별도 묶음으로 다룬다).
 */
export const isSimilarPhone = (a: string, b: string): boolean => {
  if (a === b) return false;
  if (Math.abs(a.length - b.length) > 1) return false;
  if (a.length === b.length) {
    let diff = 0;
    let firstDiff = -1;
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i]) {
        diff += 1;
        if (firstDiff < 0) firstDiff = i;
        if (diff > 2) return false;
      }
    }
    if (diff === 1) return true;
    // 인접 전치 (0102 → 0120)
    if (diff === 2 && firstDiff >= 0 && firstDiff + 1 < a.length) {
      return a[firstDiff] === b[firstDiff + 1] && a[firstDiff + 1] === b[firstDiff];
    }
    return false;
  }
  // 길이 1 차이 — 한 자리 삽입/삭제
  const longer = a.length > b.length ? a : b;
  const shorter = a.length > b.length ? b : a;
  let i = 0;
  let j = 0;
  let skipped = false;
  while (i < longer.length && j < shorter.length) {
    if (longer[i] === shorter[j]) {
      i += 1;
      j += 1;
      continue;
    }
    if (skipped) return false;
    skipped = true;
    i += 1;
  }
  return true;
};

export interface PhoneGroupMember {
  digits: string;
  entries: FlatCouponEntry[];
}

export interface PhoneGroup {
  /** 'duplicate' = 완전히 같은 번호가 여러 쿠폰에, 'similar' = 한 자리 차이 */
  kind: 'duplicate' | 'similar';
  members: PhoneGroupMember[];
  /** 이 묶음에 포함된 쿠폰 수 */
  couponCount: number;
}

/**
 * 번호가 겹치거나 비슷한 수령인 묶음을 뽑는다.
 * - duplicate: 같은 번호로 쿠폰이 2건 이상 (한 사람이 여러 개 받은 케이스 / 오기입)
 * - similar: 한 자리만 다른 번호가 함께 존재 (번호 잘못 적은 케이스 의심)
 * 검색된 주문 범위(보통 한 주문자)의 쿠폰만 대상이라 O(n²) 비교로 충분하다.
 */
export const findPhoneGroups = (entries: FlatCouponEntry[]): PhoneGroup[] => {
  const byPhone = new Map<string, FlatCouponEntry[]>();
  for (const entry of entries) {
    for (const phone of entry.phones) {
      const list = byPhone.get(phone.digits);
      if (list) {
        if (!list.includes(entry)) list.push(entry);
      } else {
        byPhone.set(phone.digits, [entry]);
      }
    }
  }
  const phones = Array.from(byPhone.keys());

  // 비슷한 번호끼리 union-find로 묶는다
  const parent = new Map<string, string>();
  const find = (x: string): string => {
    const p = parent.get(x);
    if (!p || p === x) return x;
    const root = find(p);
    parent.set(x, root);
    return root;
  };
  const union = (x: string, y: string) => {
    const rx = find(x);
    const ry = find(y);
    if (rx !== ry) parent.set(rx, ry);
  };
  phones.forEach((p) => parent.set(p, p));
  for (let i = 0; i < phones.length; i += 1) {
    for (let j = i + 1; j < phones.length; j += 1) {
      const a = phones[i];
      const b = phones[j];
      if (a && b && isSimilarPhone(a, b)) union(a, b);
    }
  }

  const clusters = new Map<string, string[]>();
  for (const phone of phones) {
    const root = find(phone);
    const list = clusters.get(root);
    if (list) list.push(phone);
    else clusters.set(root, [phone]);
  }

  const groups: PhoneGroup[] = [];
  for (const cluster of Array.from(clusters.values())) {
    const members = cluster
      .map((digits) => ({ digits, entries: byPhone.get(digits) ?? [] }))
      .sort((a, b) => a.digits.localeCompare(b.digits));
    const couponCount = new Set(
      members.flatMap((m) => m.entries.map((e) => e.coupon._id)),
    ).size;
    if (members.length >= 2) {
      groups.push({ kind: 'similar', members, couponCount });
    } else if (couponCount >= 2) {
      groups.push({ kind: 'duplicate', members, couponCount });
    }
  }
  // 비슷한 번호(오기입 의심)를 위로, 그다음 쿠폰 많은 순
  return groups.sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === 'similar' ? -1 : 1;
    return b.couponCount - a.couponCount;
  });
};
